import * as THREE from 'three'

export default class Controls
{
    constructor(_options)
    {
        this.renderer = _options.renderer
        this.camera = _options.camera
        this.vector = new THREE.Vector2()
        this.keys = new Set()
        this.pointer = null
        this.touches = new Map()
        this.pinchDistance = null
        this.radius = 64
        this.joystick = document.querySelector('.js-joystick')
        this.cursor = document.querySelector('.js-joystick-cursor')

        this.setKeyboard()
        this.setPointer()
    }

    setKeyboard()
    {
        const movementKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD']
        window.addEventListener('keydown', (_event) =>
        {
            if(movementKeys.includes(_event.code))
            {
                _event.preventDefault()
                this.keys.add(_event.code)
            }
        })
        window.addEventListener('keyup', (_event) => this.keys.delete(_event.code))
        window.addEventListener('blur', () =>
        {
            this.keys.clear()
            this.resetInput()
        })
        document.addEventListener('visibilitychange', () =>
        {
            if(document.hidden)
            {
                this.keys.clear()
                this.resetInput()
            }
        })
    }

    setPointer()
    {
        const canvas = this.renderer.domElement
        canvas.addEventListener('pointerdown', (_event) =>
        {
            if(_event.pointerType === 'mouse' && _event.button !== 0)
                return

            if(_event.pointerType === 'touch')
            {
                this.touches.set(_event.pointerId, { x: _event.clientX, y: _event.clientY })
                if(this.touches.size >= 2)
                {
                    canvas.setPointerCapture(_event.pointerId)
                    this.resetPointer()
                    this.pinchDistance = this.getTouchDistance()
                    return
                }
            }

            if(this.pointer !== null)
                return

            this.pointer = {
                id: _event.pointerId,
                startX: _event.clientX,
                startY: _event.clientY,
                x: 0,
                y: 0
            }
            canvas.setPointerCapture(_event.pointerId)
            this.joystick.style.left = `${_event.clientX}px`
            this.joystick.style.top = `${_event.clientY}px`
            this.joystick.classList.add('is-active')
            this.updateCursor(0, 0)
            document.body.classList.add('has-moved')
        })
        canvas.addEventListener('pointermove', (_event) =>
        {
            if(this.touches.has(_event.pointerId))
            {
                this.touches.set(_event.pointerId, { x: _event.clientX, y: _event.clientY })
                if(this.pinchDistance !== null && this.touches.size >= 2)
                {
                    const distance = this.getTouchDistance()
                    this.camera.zoomBy((this.pinchDistance - distance) * 0.04)
                    this.pinchDistance = distance
                    return
                }
            }

            if(!this.pointer || this.pointer.id !== _event.pointerId)
                return

            const deltaX = _event.clientX - this.pointer.startX
            const deltaY = _event.clientY - this.pointer.startY
            const length = Math.hypot(deltaX, deltaY)
            const scale = length > this.radius ? this.radius / length : 1
            const x = deltaX * scale
            const y = deltaY * scale
            this.pointer.x = Math.abs(x) < 5 ? 0 : x / this.radius
            this.pointer.y = Math.abs(y) < 5 ? 0 : - y / this.radius
            this.updateCursor(x, y)
        })
        const end = (_event) =>
        {
            if(_event.pointerType === 'touch')
            {
                this.touches.delete(_event.pointerId)
                if(this.pinchDistance !== null)
                {
                    if(this.touches.size < 2)
                        this.pinchDistance = null
                    this.resetPointer()
                }
            }
            if(this.pointer?.id === _event.pointerId)
                this.resetPointer()
        }
        canvas.addEventListener('pointerup', end)
        canvas.addEventListener('pointercancel', end)
        canvas.addEventListener('contextmenu', (_event) => _event.preventDefault())
    }

    updateCursor(_x, _y)
    {
        this.cursor.style.transform = `translate(${_x}px, ${_y}px)`
    }

    resetPointer()
    {
        this.pointer = null
        this.joystick.classList.remove('is-active')
        this.updateCursor(0, 0)
    }

    getTouchDistance()
    {
        const [first, second] = [...this.touches.values()]
        return Math.hypot(first.x - second.x, first.y - second.y)
    }

    resetInput()
    {
        this.touches.clear()
        this.pinchDistance = null
        this.resetPointer()
    }

    update()
    {
        let x = 0
        let y = 0
        if(this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1
        if(this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1
        if(this.keys.has('KeyW') || this.keys.has('ArrowUp')) y += 1
        if(this.keys.has('KeyS') || this.keys.has('ArrowDown')) y -= 1

        if(x !== 0 || y !== 0)
            this.vector.set(x, y).normalize()
        else if(this.pointer)
            this.vector.set(this.pointer.x, this.pointer.y).clampLength(0, 1)
        else
            this.vector.set(0, 0)

        if(this.vector.lengthSq() > 0)
            document.body.classList.add('has-moved')
        return this.vector
    }
}
